<template>
  <div v-if="isVisible" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container" @click.stop>
      <button @click="$emit('close')" class="close-button" type="button">
        <img src="../../assets/images/cross.svg" alt="Close" />
      </button>

      <div class="modal-header">
        <h2 class="modal-title">{{ $t('manage_package_title') }}</h2>
        <p class="modal-subtitle">{{ $t('manage_package_subtitle') }}</p>
      </div>

      <div v-if="isLoading" class="modal-body modal-loading">
        <div class="spinner"></div>
        <p>{{ $t('loading') }}...</p>
      </div>

      <div v-else-if="packageStatus" class="modal-body">
        <!-- Package info card -->
        <div class="info-card">
          <div class="info-row">
            <span class="info-label">{{ $t('current_package') }}</span>
            <span class="info-value strong">{{ packageStatus.packageName || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t('billing_period_label') }}</span>
            <span class="info-value">
              {{ packageStatus.billingPeriod === 'yearly' ? $t('yearly') : $t('monthly') }}
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t('end_date') }}</span>
            <span class="info-value">{{ formatDate(packageStatus.endDate) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t('days_remaining') }}</span>
            <span class="info-value">
              <span :class="['days-badge', daysBadgeClass]">
                {{ packageStatus.daysRemaining }} {{ $t('days') }}
              </span>
            </span>
          </div>
          <div v-if="packageStatus.amount" class="info-row">
            <span class="info-label">{{ $t('next_payment_amount') }}</span>
            <span class="info-value strong">
              {{ packageStatus.amount }} {{ packageStatus.currency || 'USD' }}
            </span>
          </div>
        </div>

        <!-- Actions -->
        <div class="actions">
          <button class="btn-primary" @click="$emit('upgrade-clicked'); $emit('close')">
            {{ $t('change_package') }}
          </button>
          <button class="btn-link" @click="$router.push('/billing/addons'); $emit('close')">
            {{ $t('buy_addon_package') }}
          </button>
        </div>
      </div>

      <div v-else class="modal-body">
        <p class="no-data-found">{{ $t('no_active_package') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PackageManageModal',

  props: {
    isVisible: { type: Boolean, default: false },
    packageStatus: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  emits: ['close', 'upgrade-clicked'],

  computed: {
    daysBadgeClass() {
      const days = this.packageStatus?.daysRemaining || 0;
      if (days <= 7) return 'days-critical';
      if (days <= 30) return 'days-warning';
      return 'days-ok';
    },
  },

  methods: {
    formatDate(value) {
      if (!value) return '-';
      try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        return d.toLocaleDateString(this.$i18n.locale === 'tr' ? 'tr-TR' : 'en-US', {
          year: 'numeric', month: 'long', day: 'numeric'
        });
      } catch (e) { return value; }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(16, 24, 40, 0.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 480px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
}

.close-button {
  position: absolute;
  top: 16px; right: 16px;
  background: none; border: none; cursor: pointer;
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
}
.close-button:hover { background: #F2F4F7; }

.modal-header { margin-bottom: 24px; }
.modal-title {
  font-size: 20px;
  font-family: euclid_semi_bold, sans-serif;
  color: #101828; margin: 0;
}
.modal-subtitle {
  font-size: 14px;
  color: #667085;
  margin: 4px 0 0 0;
  font-family: euclid_regular, sans-serif;
}

.modal-body { display: flex; flex-direction: column; gap: 20px; }

.info-card {
  background: #F9FAFB;
  border: 1px solid #EAECF0;
  border-radius: 12px;
  padding: 16px;
  display: flex; flex-direction: column; gap: 12px;
}

.info-row {
  display: flex; justify-content: space-between; align-items: center;
}

.info-label {
  font-size: 13px;
  color: #667085;
  font-family: euclid_regular, sans-serif;
}

.info-value {
  font-size: 13px;
  color: #101828;
  font-family: euclid_medium, sans-serif;
}

.info-value.strong {
  font-family: euclid_semi_bold, sans-serif;
  font-size: 14px;
}

.days-badge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-family: euclid_medium, sans-serif;
}
.days-ok { background: #ECFDF3; color: #027A48; }
.days-warning { background: #FEF0C7; color: #B54708; }
.days-critical { background: #FEE4E2; color: #B42318; }

.modal-loading {
  align-items: center;
  padding: 60px 20px;
  gap: 16px;
}

.modal-loading p {
  margin: 0;
  color: #667085;
  font-size: 14px;
  font-family: euclid_regular, sans-serif;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #EAECF0;
  border-top-color: #4A68F8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.actions {
  display: flex; flex-direction: column; gap: 8px;
}

.btn-primary {
  padding: 12px;
  background: #101828;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: euclid_medium, sans-serif;
  cursor: pointer;
}
.btn-primary:hover { background: #1D2939; }

.btn-link {
  padding: 10px;
  background: none;
  color: #4A68F8;
  border: none;
  font-size: 13px;
  font-family: euclid_medium, sans-serif;
  cursor: pointer;
  text-align: center;
}
.btn-link:hover { text-decoration: underline; }

.no-data-found {
  text-align: center;
  padding: 40px;
  color: #667085;
  font-size: 14px;
}
</style>
